import { useShallowEffect } from '@mantine/hooks';
import { clientsApi } from '@modules/clients/entities/client/api';
import { toast } from '@huspy/forge/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ClientsListSearchQuery } from '@modules/clients/presentation/routes';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';

const useClientsList = (q: ClientsListSearchQuery & { limit?: string }, companyId?: string) => {
  const [query, setQuery] = useState<ClientsListSearchQuery & { limit?: string }>(q);
  const { isFeatureActive } = useFeatureFlags();
  const isClientCompanyActive = isFeatureActive('clients.companyClients');
  const {
    data, isLoading, error, refetch, isRefetching,
  } = useQuery({
    queryKey: ['clientsList', query.page, query],
    queryFn: () => (isClientCompanyActive
      ? clientsApi.getCompanyClientsList(companyId!, query)
      : clientsApi.getClientsList(query)),
    staleTime: 1_800_000,
    placeholderData: keepPreviousData,
    enabled: isClientCompanyActive ? !!companyId : true,
  });

  // https://mantine.dev/hooks/use-shallow-effect/
  useShallowEffect(() => {
    setQuery(q);
  }, [q]);

  useEffect(() => {
    if (error) {
      toast('error', { message: error.message });
    }
  }, [error]);

  return {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
  };
};

export default useClientsList;
